import React, { useEffect, useState } from "react";
import axios from "axios";
import { useApp } from "../contexts/AppContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Plus, Trash2, CreditCard, DollarSign, Pencil, Eye } from "lucide-react";
import { formatCurrency, API_URL } from "../lib/utils";


export default function LoansPage() {
  const { companies, locations, fetchCompanies, fetchLocations } = useApp();
  const [loans, setLoans] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [paymentDialog, setPaymentDialog] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentComment, setPaymentComment] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [historyDialog, setHistoryDialog] = useState(null);
  const [editDialog, setEditDialog] = useState(null);
  const [editForm, setEditForm] = useState({ total_amount: "", monthly_installment: "", notes: "" });
  const [dialogCompany, setDialogCompany] = useState("");
  const [dialogLocation, setDialogLocation] = useState("");
  const [dialogEmployees, setDialogEmployees] = useState([]);

  const [form, setForm] = useState({
    employee_id: "",
    total_amount: "",
    monthly_installment: "",
    notes: ""
  });

  useEffect(() => {
    fetchCompanies();
    fetchLocations();
    loadLoans();
    loadAllEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchCompanies, fetchLocations]);

  useEffect(() => {
    loadDialogEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogCompany, dialogLocation]);

  const loadLoans = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/loans`, { withCredentials: true });
      setLoans(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadAllEmployees = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/employees`, { withCredentials: true });
      setEmployees(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadDialogEmployees = async () => {
    try {
      const params = {};
      if (dialogLocation) params.location_id = dialogLocation;
      else if (dialogCompany) params.company_id = dialogCompany;
      const res = await axios.get(`${API_URL}/api/employees`, { params, withCredentials: true });
      setDialogEmployees(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCreate = async () => {
    try {
      await axios.post(`${API_URL}/api/loans`, {
        employee_id: form.employee_id,
        total_amount: parseFloat(form.total_amount),
        monthly_installment: parseFloat(form.monthly_installment),
        notes: form.notes || null
      }, { withCredentials: true });
      setDialogOpen(false);
      setForm({ employee_id: "", total_amount: "", monthly_installment: "", notes: "" });
      setDialogCompany("");
      setDialogLocation("");
      loadLoans();
    } catch (err) {
      alert("Errore: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (loanId) => {
    if (!window.confirm("Eliminare questo prestito?")) return;
    try {
      await axios.delete(`${API_URL}/api/loans/${loanId}`, { withCredentials: true });
      loadLoans();
    } catch (err) {
      alert("Errore: " + (err.response?.data?.detail || err.message));
    }
  };

  const handlePayment = async () => {
    if (!paymentDialog || !paymentDate) return;
    const amount = parseFloat(paymentAmount) || 0;
    try {
      await axios.post(`${API_URL}/api/loans/${paymentDialog.id}/payment`, {
        amount,
        comment: paymentComment || "",
        date: paymentDate
      }, { withCredentials: true });
      setPaymentDialog(null);
      setPaymentAmount("");
      setPaymentComment("");
      setPaymentDate("");
      loadLoans();
    } catch (err) {
      alert("Errore: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleEditSave = async () => {
    if (!editDialog) return;
    try {
      await axios.put(`${API_URL}/api/loans/${editDialog.id}`, {
        total_amount: parseFloat(editForm.total_amount) || 0,
        monthly_installment: parseFloat(editForm.monthly_installment) || 0,
        notes: editForm.notes || null
      }, { withCredentials: true });
      setEditDialog(null);
      loadLoans();
    } catch (err) {
      alert("Errore: " + (err.response?.data?.detail || err.message));
    }
  };

  const getEmployeeName = (empId) => {
    const emp = employees.find(e => e.id === empId);
    if (emp) return `${emp.last_name} ${emp.first_name}`;
    return empId;
  };

  const formatDate = (isoStr) => {
    if (!isoStr) return "-";
    try {
      return new Date(isoStr).toLocaleDateString("it-IT", {
        day: "2-digit", month: "2-digit", year: "numeric"
      });
    } catch {
      return isoStr;
    }
  };

  const formatDateTime = (isoStr) => {
    if (!isoStr) return "-";
    try {
      return new Date(isoStr).toLocaleDateString("it-IT", {
        day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"
      });
    } catch {
      return isoStr;
    }
  };

  const dialogFilteredLocations = dialogCompany
    ? locations.filter(l => l.company_id === dialogCompany)
    : locations;

  const totalCredit = loans
    .filter(l => l.is_active)
    .reduce((sum, l) => sum + (l.total_amount - (l.amount_paid || 0)), 0);

  const activeLoans = loans.filter(l => l.is_active);
  const completedLoans = loans.filter(l => !l.is_active);

  return (
    <div className="space-y-6" data-testid="loans-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-outfit tracking-tight text-[#09090B]">
            Prestiti
          </h1>
          <p className="text-[#52525B] font-ibm-plex mt-1">
            Gestisci i prestiti ai dipendenti
          </p>
        </div>
        <Button
          data-testid="new-loan-button"
          onClick={() => setDialogOpen(true)}
          className="bg-black hover:bg-gray-800 text-white font-ibm-plex"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuovo Prestito
        </Button>
      </div>

      {/* Summary Card */}
      <Card className="border border-[#E4E4E7] bg-[#09090B] text-white">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400 font-ibm-plex">Credito Totale Azienda</p>
              <p className="text-3xl font-bold font-outfit mt-1" data-testid="total-credit">
                {formatCurrency(totalCredit)}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-400 font-ibm-plex">Prestiti attivi</p>
              <p className="text-2xl font-bold font-outfit mt-1">{activeLoans.length}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Active Loans */}
      <Card className="border border-[#E4E4E7]">
        <CardHeader>
          <CardTitle className="font-outfit text-lg">Prestiti Attivi</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {activeLoans.length === 0 ? (
            <div className="py-8 text-center text-[#52525B] font-ibm-plex">
              Nessun prestito attivo
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F4F4F5]">
                    <TableHead className="font-ibm-plex font-semibold">Dipendente</TableHead>
                    <TableHead className="font-ibm-plex font-semibold text-right">Importo Totale</TableHead>
                    <TableHead className="font-ibm-plex font-semibold text-right">Rata Mensile</TableHead>
                    <TableHead className="font-ibm-plex font-semibold text-right">Versato</TableHead>
                    <TableHead className="font-ibm-plex font-semibold text-right">Residuo</TableHead>
                    <TableHead className="font-ibm-plex font-semibold">Note</TableHead>
                    <TableHead className="font-ibm-plex font-semibold w-[120px]">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activeLoans.map(loan => {
                    const remaining = loan.total_amount - (loan.amount_paid || 0);
                    return (
                      <TableRow key={loan.id} data-testid={`loan-row-${loan.id}`}>
                        <TableCell className="font-ibm-plex font-medium">
                          {getEmployeeName(loan.employee_id)}
                        </TableCell>
                        <TableCell className="font-ibm-plex-mono text-right">
                          {formatCurrency(loan.total_amount)}
                        </TableCell>
                        <TableCell className="font-ibm-plex-mono text-right">
                          {formatCurrency(loan.monthly_installment)}
                        </TableCell>
                        <TableCell className="font-ibm-plex-mono text-right text-green-600">
                          {formatCurrency(loan.amount_paid || 0)}
                        </TableCell>
                        <TableCell className="font-ibm-plex-mono text-right text-red-600 font-bold">
                          {formatCurrency(remaining)}
                        </TableCell>
                        <TableCell className="font-ibm-plex text-sm text-[#52525B] max-w-[200px] truncate">
                          {loan.notes || "-"}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              data-testid={`history-loan-${loan.id}`}
                              onClick={() => setHistoryDialog(loan)}
                              title="Storico"
                            >
                              <Eye className="w-4 h-4 text-[#52525B]" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              data-testid={`edit-loan-${loan.id}`}
                              onClick={() => {
                                setEditDialog(loan);
                                setEditForm({
                                  total_amount: loan.total_amount?.toString() || "",
                                  monthly_installment: loan.monthly_installment?.toString() || "",
                                  notes: loan.notes || ""
                                });
                              }}
                              title="Revisiona"
                            >
                              <Pencil className="w-4 h-4 text-blue-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              data-testid={`record-payment-${loan.id}`}
                              onClick={() => { setPaymentDialog(loan); setPaymentAmount(loan.monthly_installment?.toString() || ""); setPaymentComment(""); setPaymentDate(new Date().toISOString().split("T")[0]); }}
                              title="Registra Pagamento"
                            >
                              <DollarSign className="w-4 h-4 text-green-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              data-testid={`delete-loan-${loan.id}`}
                              onClick={() => handleDelete(loan.id)}
                              title="Elimina"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Completed Loans */}
      {completedLoans.length > 0 && (
        <Card className="border border-[#E4E4E7]">
          <CardHeader>
            <CardTitle className="font-outfit text-lg text-[#52525B]">Prestiti Completati</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F4F4F5]">
                    <TableHead className="font-ibm-plex font-semibold">Dipendente</TableHead>
                    <TableHead className="font-ibm-plex font-semibold text-right">Importo</TableHead>
                    <TableHead className="font-ibm-plex font-semibold">Note</TableHead>
                    <TableHead className="font-ibm-plex font-semibold w-[80px]">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {completedLoans.map(loan => (
                    <TableRow key={loan.id} className="opacity-60">
                      <TableCell className="font-ibm-plex">{getEmployeeName(loan.employee_id)}</TableCell>
                      <TableCell className="font-ibm-plex-mono text-right">{formatCurrency(loan.total_amount)}</TableCell>
                      <TableCell className="font-ibm-plex text-sm">{loan.notes || "-"}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(loan.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* New Loan Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Nuovo Prestito
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-ibm-plex text-xs text-[#52525B]">Filtra per azienda</Label>
                <Select value={dialogCompany || "all"} onValueChange={(v) => { setDialogCompany(v === "all" ? "" : v); setDialogLocation(""); setForm({...form, employee_id: ""}); }}>
                  <SelectTrigger className="font-ibm-plex">
                    <SelectValue placeholder="Tutte le aziende" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutte le aziende</SelectItem>
                    {companies.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="font-ibm-plex text-xs text-[#52525B]">Filtra per sede</Label>
                <Select value={dialogLocation || "all"} onValueChange={(v) => { setDialogLocation(v === "all" ? "" : v); setForm({...form, employee_id: ""}); }}>
                  <SelectTrigger className="font-ibm-plex">
                    <SelectValue placeholder="Tutte le sedi" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutte le sedi</SelectItem>
                    {dialogFilteredLocations.map(l => (
                      <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="font-ibm-plex">Dipendente</Label>
              <Select value={form.employee_id || "placeholder"} onValueChange={(v) => setForm({ ...form, employee_id: v === "placeholder" ? "" : v })}>
                <SelectTrigger data-testid="loan-employee-select" className="font-ibm-plex">
                  <SelectValue placeholder="Seleziona dipendente" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="placeholder" disabled>Seleziona dipendente...</SelectItem>
                  {dialogEmployees.map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.last_name} {e.first_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-ibm-plex">Importo Totale (€)</Label>
                <Input
                  data-testid="loan-amount-input"
                  type="number"
                  step="0.01"
                  value={form.total_amount}
                  onChange={(e) => setForm({ ...form, total_amount: e.target.value })}
                  className="font-ibm-plex"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-ibm-plex">Rata Mensile (€)</Label>
                <Input
                  data-testid="loan-installment-input"
                  type="number"
                  step="0.01"
                  value={form.monthly_installment}
                  onChange={(e) => setForm({ ...form, monthly_installment: e.target.value })}
                  className="font-ibm-plex"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="font-ibm-plex">Note</Label>
              <Textarea
                data-testid="loan-notes-input"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="font-ibm-plex"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="font-ibm-plex">
              Annulla
            </Button>
            <Button
              data-testid="save-loan-button"
              onClick={handleCreate}
              disabled={!form.employee_id || !form.total_amount || !form.monthly_installment}
              className="bg-black hover:bg-gray-800 text-white font-ibm-plex"
            >
              Crea Prestito
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={!!paymentDialog} onOpenChange={(open) => { if (!open) { setPaymentDialog(null); setPaymentComment(""); setPaymentDate(""); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-outfit">Registra Pagamento</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-[#52525B] font-ibm-plex">
              Prestito: {paymentDialog && getEmployeeName(paymentDialog.employee_id)}
            </p>
            <div className="space-y-2">
              <Label className="font-ibm-plex">Data Versamento *</Label>
              <Input
                data-testid="payment-date-input"
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                className="font-ibm-plex"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-ibm-plex">Importo Pagamento (€)</Label>
              <Input
                data-testid="payment-amount-input"
                type="number"
                step="0.01"
                min="0"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                className="font-ibm-plex"
              />
              <p className="text-xs text-[#71717A] font-ibm-plex">
                Puoi impostare anche €0 per saltare la rata
              </p>
            </div>
            <div className="space-y-2">
              <Label className="font-ibm-plex">Commento (opzionale)</Label>
              <Input
                data-testid="payment-comment-input"
                type="text"
                value={paymentComment}
                onChange={(e) => setPaymentComment(e.target.value)}
                placeholder="Es: rata marzo, anticipo..."
                className="font-ibm-plex"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPaymentDialog(null); setPaymentComment(""); setPaymentDate(""); }} className="font-ibm-plex">
              Annulla
            </Button>
            <Button
              data-testid="confirm-payment-button"
              onClick={handlePayment}
              disabled={paymentAmount === "" || !paymentDate}
              className="bg-green-600 hover:bg-green-700 text-white font-ibm-plex"
            >
              Conferma Pagamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Loan Dialog */}
      <Dialog open={!!editDialog} onOpenChange={(open) => { if (!open) setEditDialog(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <Pencil className="w-5 h-5" />
              Revisiona Prestito
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-[#52525B] font-ibm-plex">
              Dipendente: <strong>{editDialog && getEmployeeName(editDialog.employee_id)}</strong>
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-ibm-plex">Importo Totale (€)</Label>
                <Input
                  data-testid="edit-loan-amount"
                  type="number"
                  step="0.01"
                  value={editForm.total_amount}
                  onChange={(e) => setEditForm({ ...editForm, total_amount: e.target.value })}
                  className="font-ibm-plex"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-ibm-plex">Rata Mensile (€)</Label>
                <Input
                  data-testid="edit-loan-installment"
                  type="number"
                  step="0.01"
                  value={editForm.monthly_installment}
                  onChange={(e) => setEditForm({ ...editForm, monthly_installment: e.target.value })}
                  className="font-ibm-plex"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="font-ibm-plex">Note</Label>
              <Textarea
                data-testid="edit-loan-notes"
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                className="font-ibm-plex"
                rows={2}
              />
            </div>
            {editDialog && (
              <div className="rounded-md bg-[#F4F4F5] p-3 text-sm font-ibm-plex text-[#52525B]">
                Versato finora: <strong className="text-green-600">{formatCurrency(editDialog.amount_paid || 0)}</strong>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(null)} className="font-ibm-plex">
              Annulla
            </Button>
            <Button
              data-testid="save-edit-loan-button"
              onClick={handleEditSave}
              disabled={!editForm.total_amount || !editForm.monthly_installment}
              className="bg-black hover:bg-gray-800 text-white font-ibm-plex"
            >
              Salva Modifiche
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={!!historyDialog} onOpenChange={(open) => { if (!open) setHistoryDialog(null); }}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Storico Prestito
            </DialogTitle>
          </DialogHeader>
          {historyDialog && (
            <div className="space-y-4 py-2 overflow-y-auto flex-1">
              {/* Loan Info */}
              <div className="rounded-md border border-[#E4E4E7] p-4 space-y-2">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-xs text-[#71717A] font-ibm-plex">Dipendente</p>
                    <p className="font-ibm-plex font-semibold">{getEmployeeName(historyDialog.employee_id)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-[#71717A] font-ibm-plex">Data creazione</p>
                    <p className="font-ibm-plex font-medium">{formatDate(historyDialog.created_at)}</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 pt-2 border-t border-[#E4E4E7]">
                  <div>
                    <p className="text-xs text-[#71717A] font-ibm-plex">Importo Totale</p>
                    <p className="font-ibm-plex-mono font-semibold">{formatCurrency(historyDialog.total_amount)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#71717A] font-ibm-plex">Versato</p>
                    <p className="font-ibm-plex-mono font-semibold text-green-600">{formatCurrency(historyDialog.amount_paid || 0)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#71717A] font-ibm-plex">Residuo</p>
                    <p className="font-ibm-plex-mono font-semibold text-red-600">{formatCurrency(historyDialog.total_amount - (historyDialog.amount_paid || 0))}</p>
                  </div>
                </div>
                {historyDialog.notes && (
                  <div className="pt-2 border-t border-[#E4E4E7]">
                    <p className="text-xs text-[#71717A] font-ibm-plex">Note</p>
                    <p className="font-ibm-plex text-sm">{historyDialog.notes}</p>
                  </div>
                )}
              </div>

              {/* Payments List */}
              <div>
                <p className="font-ibm-plex font-semibold text-sm mb-2">
                  Versamenti ({(historyDialog.payments || []).length})
                </p>
                {(!historyDialog.payments || historyDialog.payments.length === 0) ? (
                  <div className="text-center py-6 text-[#71717A] font-ibm-plex text-sm border border-dashed border-[#E4E4E7] rounded-md">
                    Nessun versamento registrato
                  </div>
                ) : (
                  <div className="space-y-2">
                    {[...(historyDialog.payments || [])].reverse().map((p, idx) => (
                      <div
                        key={p.id || idx}
                        data-testid={`payment-history-${idx}`}
                        className="flex items-start justify-between gap-3 p-3 rounded-md bg-[#F4F4F5] border border-[#E4E4E7]"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-ibm-plex-mono font-bold text-green-700">
                              {formatCurrency(p.amount)}
                            </span>
                            {p.amount === 0 && (
                              <span className="text-[10px] font-ibm-plex bg-[#E4E4E7] text-[#52525B] px-1.5 py-0.5 rounded">
                                SALTATA
                              </span>
                            )}
                          </div>
                          {p.comment && (
                            <p className="text-xs text-[#52525B] font-ibm-plex mt-1 truncate">
                              {p.comment}
                            </p>
                          )}
                        </div>
                        <span className="text-xs text-[#71717A] font-ibm-plex whitespace-nowrap">
                          {formatDate(p.date)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryDialog(null)} className="font-ibm-plex">
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
